import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 saniye timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Token'ı header'a ekle
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Rate-limit for the generic 5xx toast (see interceptor below).
let serverErrorToastAt = 0;

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Sliding sessions: the server hands back a renewed token once the
    // current one passes its renewal age. Store it so the user is not
    // logged out at the absolute expiry while they are active.
    const renewed = response.headers?.['x-session-token'];
    if (renewed) {
      try { localStorage.setItem('token', renewed); } catch (_) { /* ignore */ }
    }
    return response;
  },
  (error) => {
    const message = error.response?.data?.error || error.message || 'An error occurred';
    const url = error.config?.url || '';

    // Automatic error messages - skip showing errors for certain endpoints that handle errors gracefully
    const silentErrors = ['/convert-currency']; // Endpoints that handle their own errors
    // Per-call opt-out: widgets and other components that render their own
    // empty states pass `silent: true` in the axios config to skip the
    // global toast. Avoids "you do not have permission" spam when a
    // dashboard widget probes an endpoint the operator's role doesn't have.
    const silentRequest = error.config?.silent === true;
    const shouldShowError = !silentRequest && !silentErrors.some(endpoint => url.includes(endpoint));

    if (shouldShowError) {
      if (error.response?.status >= 500) {
        // Identify the failing call in devtools — the toast is generic on
        // purpose, but "which endpoint 500s intermittently?" is otherwise
        // undiagnosable from the browser.
        console.error(
          `[api] ${error.response.status} from ${String(error.config?.method || '').toUpperCase()} ${url}`,
          error.response?.data
        );
        // Throttle: one server-error toast per 10s. A backend restart or
        // flaky endpoint under 10s polling otherwise stacks identical
        // toasts faster than they expire.
        if (Date.now() - serverErrorToastAt > 10000) {
          serverErrorToastAt = Date.now();
          toast.error('Server error occurred. Please try again later.');
        }
      } else if (error.response?.status === 404) {
        toast.error('Requested resource not found.');
      } else if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission for this operation.');
      } else if (error.response?.status === 400) {
        toast.error(message);
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again.');
      } else if (error.code === 'NETWORK_ERROR') {
        toast.error('Network connection error. Please check your internet connection.');
      }
    }

    return Promise.reject(error);
  }
);

// Auth API servisleri
export const authAPI = {
  // Login
  login: (credentials) => api.post('/auth/login', credentials),

  // Logout
  logout: () => api.post('/auth/logout'),

  // Mevcut kullanıcı bilgileri
  getMe: () => api.get('/auth/me'),
};

// Lead API servisleri
export const leadsAPI = {
  // Tüm lead'leri getir
  getLeads: (params = {}) => api.get('/leads', { params }),

  // Lead'leri Excel olarak dışa aktar (filtrelerle birlikte) — blob döner
  exportLeads: (params = {}) => api.get('/leads/export', {
    params,
    responseType: 'blob',
    timeout: 300000, // 5 dakika (büyük dışa aktarımlar için)
  }),

  // Tek lead getir
  getLead: (id) => api.get(`/leads/${id}`),

  // Yeni lead oluştur
  createLead: (data) => api.post('/leads', data),

  // Lead güncelle
  updateLead: (id, data) => api.put(`/leads/${id}`, data),

  // Lead sil
  deleteLead: (id) => api.delete(`/leads/${id}`),

  // Mail gönder
  sendEmail: (id, data) => api.post(`/leads/${id}/send-email`, data),

  // SMS gönder
  sendSMS: (id, data) => api.post(`/leads/${id}/send-sms`, data),
  // Lift Do Not Call with the reason on record (server/services/contactPolicy.js).
  clearDnc: (id, reason) => api.post(`/leads/${id}/dnc/clear`, { reason }),
  // Hide the "Re-engaged" Pipeline badge (server/services/reengagement.js).
  dismissReengaged: (id) => api.post(`/leads/${id}/reengaged/dismiss`),
  // Phone numbers on a lead (server/services/leadPhones.js).
  addPhone: (id, number, label) => api.post(`/leads/${id}/phones`, { number, label }),
  updatePhone: (id, last10, data) => api.patch(`/leads/${id}/phones/${last10}`, data),
  removePhone: (id, last10) => api.delete(`/leads/${id}/phones/${last10}`),

  // WhatsApp Cloud API gönder
  sendWhatsApp: (id, data) => api.post(`/leads/${id}/send-whatsapp`, data),
  sendWhatsAppTemplate: (id, data) => api.post(`/leads/${id}/send-whatsapp-template`, data),
  getWhatsAppWindow: (id) => api.get(`/leads/${id}/whatsapp/window`),

  // Messenger / IG DM gönder
  sendMessenger: (id, data) => api.post(`/leads/${id}/send-messenger`, data),

  // Dosya ekleri — çoklu yükleme (multipart); indirme blob döner
  uploadAttachments: (id, files) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return api.post(`/leads/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 dakika (büyük dosyalar için)
    });
  },
  downloadAttachment: (id, attachmentId) =>
    api.get(`/leads/${id}/attachments/${attachmentId}/download`, { responseType: 'blob' }),
  getAttachmentThumbnail: (id, attachmentId) =>
    api.get(`/leads/${id}/attachments/${attachmentId}/thumbnail`, { responseType: 'blob' }),
  deleteAttachment: (id, attachmentId) => api.delete(`/leads/${id}/attachments/${attachmentId}`),

  // Not ekle
  addNote: (id, content, customFields = null) => api.post(`/leads/${id}/notes`, customFields ? { content, customFields } : { content }),

  // Not sil
  deleteNote: (id, noteId) => api.delete(`/leads/${id}/notes/${noteId}`),

  // Toplu işlemler
  bulkAction: (data) => api.post('/leads/bulk', data),

  // İstatistikler
  getStats: (params = {}) => api.get('/leads/stats/overview', { params }),

  // Currency conversion
  convertCurrency: (data) => api.post('/leads/convert-currency', data),

  // Organization bilgilerini getir
  getOrganization: (id) => api.get(`/organizations/info`),

  // Telefon numarasına göre lead ara
  searchByPhone: (phone) => api.get(`/leads/search/phone/${phone}`),

  // Email'e göre lead ara
  searchByEmail: (email) => api.get(`/leads/search/email/${email}`),

  // Telefon numarasına göre note ekle
  addNoteByPhone: (phone, content) => api.post(`/leads/notes/phone/${phone}`, { content }),

  // Email'e göre note ekle
  addNoteByEmail: (email, content) => api.post(`/leads/notes/email/${email}`, { content }),
};

// Bulk Email API servisleri
export const bulkEmailAPI = {
  // Kampanyaları listele
  getCampaigns: (params = {}) => api.get('/bulk-email', { params }),

  // Kampanya oluştur
  createCampaign: (data) => api.post('/bulk-email', data),

  // Kampanya detayları
  getCampaign: (id) => api.get(`/bulk-email/${id}`),

  // Kampanyayı başlat
  startCampaign: (id) => api.post(`/bulk-email/${id}/start`),

  // Kampanyayı iptal et
  cancelCampaign: (id) => api.post(`/bulk-email/${id}/cancel`),

  // Kampanyayı sil
  deleteCampaign: (id) => api.delete(`/bulk-email/${id}`),

  // Alıcı listesi
  getRecipients: (id, params = {}) => api.get(`/bulk-email/${id}/recipients`, { params }),


};

// Upload API servisleri
export const uploadAPI = {
  // Tek kartvizit yükle
  uploadBusinessCard: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/business-card', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Toplu kartvizit yükle
  uploadBusinessCardsBulk: (files) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });
    return api.post('/upload/business-cards-bulk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Dosya durumu kontrolü
  checkFileStatus: (filename) => api.get(`/upload/status/${filename}`),
};

// Activities API servisleri
export const activitiesAPI = {
  // Son aktiviteleri getir
  getActivities: (params = {}) => api.get('/activities', { params }),

  // Lead'e ait aktiviteleri getir
  getLeadActivities: (leadId, params = {}) => api.get(`/activities/lead/${leadId}`, { params }),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

// Organization API services
export const organizationsAPI = {
  // List active users in the caller's organization (for assignment pickers)
  getUsers: () => api.get('/organizations/users'),
  // Org info (name, settings incl. timezone, subscription…)
  getInfo: () => api.get('/organizations/info'),
  // Merge-update org settings (admin only), e.g. { timezone: 'America/Los_Angeles' }
  updateSettings: (settings) => api.put('/organizations/settings', { settings }),
};

// CSV Import API servisleri
export const csvImportAPI = {
  // CSV'den lead'leri import et
  importLeads: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/csv/import-leads', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 dakika (büyük dosyalar için)
    });
  },

  // CSV önizleme
  preview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/csv/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Flexible import wizard — step 1: inspect the file and get an
  // auto-suggested column mapping. The server returns columns +
  // sampleRows + suggestedMapping (Your column → Our field).
  analyze: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/csv/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Flexible import wizard — step 2: commit the import with the
  // operator-confirmed mapping. updateExisting toggles upsert.
  importMapped: (file, mapping, updateExisting) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    formData.append('updateExisting', String(!!updateExisting));
    return api.post('/csv/import-mapped', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
};

// OpenDental API servisleri
// Settings + appointment-booking endpoints for the per-org OpenDental
// integration. booking-options and slots pass `silent: true` so the
// global axios error toast stays quiet — the booking modal renders its
// own inline error / degraded-mode UI when OpenDental is unreachable.
export const opendentalAPI = {
  // --- Settings (admin) — org-level state + branch list ---
  getSettings: () => api.get('/opendental/settings'),

  // --- Branch CRUD (admin) ---
  addLocation: (data) => api.post('/opendental/locations', data),
  updateLocation: (locId, data) => api.put(`/opendental/locations/${locId}`, data),
  archiveLocation: (locId) => api.delete(`/opendental/locations/${locId}`),
  testLocation: (locId) => api.post(`/opendental/locations/${locId}/test-connection`),

  // --- Booking ---
  // Configured branches for the modal's branch picker.
  getBookingBranches: () => api.get('/opendental/booking-branches', { silent: true }),

  // Provider / operatory / appointment-type options for a branch.
  getBookingOptions: (locId) => api.get(`/opendental/locations/${locId}/booking-options`, { silent: true }),

  // Open slots for a branch on a given day.
  getSlots: (locId, params = {}) => api.get(`/opendental/locations/${locId}/slots`, { params, silent: true }),

  // A provider's already-booked appointments for a day — powers the
  // non-blocking double-booking warning in the modal.
  getProviderAppointments: (locId, params = {}) =>
    api.get(`/opendental/locations/${locId}/provider-appointments`, { params, silent: true }),

  // Create the appointment (live or pending). body carries locationId.
  bookAppointment: (body) => api.post('/opendental/book-appointment', body),

  // Replay a pending/failed lead's stored intent against its branch.
  retrySync: (leadId) => api.post(`/opendental/retry-sync/${leadId}`),
};

// Unsubscribe API servisleri
export const unsubscribeAPI = {
  // Unsubscribe işlemi
  unsubscribe: (token) => api.get(`/unsubscribe/${token}`),
};

// Notifications API servisleri
export const notificationsAPI = {
  // Bildirimleri getir
  getNotifications: (params = {}) => api.get('/notifications', { params }),

  // Okunmamış bildirim sayısını getir
  getUnreadCount: () => api.get('/notifications/unread-count'),

  // Bildirimi okundu olarak işaretle
  markAsRead: (id) => api.put(`/notifications/${id}/read`),

  // Tüm bildirimleri okundu olarak işaretle
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  markTypeAsRead: (type) => api.put(`/notifications/mark-type-read/${type}`),
};

export default api; 